import isEqual from 'lodash/isEqual';
import { mapState } from 'vuex';
import CoreSplitButton from '@/components/core/splitButton.vue';
export default {
  name: 'CoreAccountFilter',
  components: {
    CoreSplitButton
  },
  props: {
    // The default list of account ids of the filter
    default: {
      type: Array,
      required: false,
      default: () => []
    },
    // Set specific accounts to be selected, but do not return to these when resetting
    selected: {
      type: Array,
      required: false,
      default: () => []
    },
    // If true, the component will allow multiple accounts to be selected
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    // If true, the component will allow the selected accounts to be empty
    allowEmpty: {
      type: Boolean,
      required: false,
      default: false
    },
    // If true, the component will use the niota 1.0 API to fetch account data
    n1Accounts: {
      type: Boolean,
      required: false,
      default: false
    },
    // If true, the trigger button will contain the icon only with no text label
    iconOnly: {
      type: Boolean,
      required: false,
      default: false
    },
    // If true, the reset button will be hidden
    hideReset: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      accounts: [],
      isLoading: false,
      searchInput: null,
      selectedAccountIds: [],
      dynamicKey: 'key'
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled']),
    /**
     * Is true if selectedAccountIds array has one or more entries, otherwise false.
     */
    hasSelection() {
      return this.selectedAccountIds.length > 0;
    },
    /**
     * Is true while props iconOnly and hideReset are both true, otherwise false.
     */
    isSingleButton() {
      return this.iconOnly && this.hideReset;
    },
    /**
     * A filtered version of searchResults array, which does not contain any selected accounts.
     */
    searchResultsWithoutSelected() {
      return this.searchResults.filter(item => !this.selectedAccountIds.includes(item.id));
    },
    /**
     * A filtered version of the accounts array, which only contains selected accounts.
     */
    selectedAccounts() {
      return this.accounts.filter(item => this.selectedAccountIds.includes(item.id));
    },
    /**
     * A filtered version of the accounts array, which only contains accounts which match the search input.
     * If no search input is given, the full accounts array is returned.
     */
    searchResults() {
      if (this.searchInput) {
        return this.accounts.filter(item => {
          return this.searchInput.toLowerCase().split(' ').every(v => item.name.toLowerCase().includes(v) || item.id === parseInt(v, 10));
        });
      } else {
        return this.accounts;
      }
    }
  },
  watch: {
    selected: {
      handler: function (accounts) {
        this.selectedAccountIds = this.sanitizeSelectedIds(accounts);
        this.storeAndEmitSelection();
      },
      deep: true
    }
  },
  mounted() {
    this.fetchAccounts();
  },
  methods: {
    /**
     * Makes sure the selectedIds are present in the current accounts array, filters out invalid ids.
     * Also makes sure to only keep a single id if multiple prop is false.
     * @param {number[]} selectedIds
     * @returns {number[]}
     */
    sanitizeSelectedIds(selectedIds) {
      const filtered = this.accounts.filter(item => selectedIds.includes(item.id)).map(item => item.id);
      return this.multiple ? filtered : [filtered.pop()];
    },
    /**
     * Take the given array of account ids and return the corresponding account objects
     * @param {number[]} ids
     * @returns {number[]}
     */
    getAccountsFromIds(ids) {
      return this.accounts.filter(item => ids.includes(item.id));
    },
    /**
     * Commit selectedAccountIds to VueX store and emit 'change' event with the according account objects
     * @returns {void}
     */
    storeAndEmitSelection() {
      const mutationName = this.n1Accounts ? 'app/n1Accounts' : 'app/n2Accounts';
      this.$store.commit(mutationName, {
        selectedIds: this.selectedAccountIds
      });
      this.$emit('change', this.getAccountsFromIds(this.selectedAccountIds));
    },
    /**
     * Initialize account selection on mounted()
     * Use account ids from the store if available, otherwise use the default props list of ids
     * Emit and store the selection so the parent component can react to it
     */
    initializeSelection() {
      let selected = this.n1Accounts ? this.$store.state.app.n1Accounts.selectedIds : this.$store.state.app.n2Accounts.selectedIds;
      if (selected.length === 0) {
        selected = this.default;
      }
      this.selectedAccountIds = this.sanitizeSelectedIds(selected);
      this.storeAndEmitSelection();
      this.$emit('ready');
    },
    /**
     * Called whenever the menu closes or opens and resets the search input to null on close
     * @param {Boolean} isOpen
     * @returns {void}
     */
    handleDropdownChange(isOpen) {
      // reset search input when dropdown is closed
      if (!isOpen) {
        this.searchInput = null;
      }
    },
    /**
     * Handling clicks on list items in the dropdown menu.
     * Providing deselect as true will remove the clicked item from current selection, otherwise it will be added.
     * If the new selection is empty and allowEmpty is false, the selection will be reset to the default value.
     * Stores and emits the new selection.
     *
     * @param {Number} accountId
     * @param {Boolean} [deselect=false]
     * @returns {void}
     */
    handleListItemClicked(accountId, deselect = false) {
      this.dynamicKey = Date.now();
      let newSelected;
      if (deselect) {
        newSelected = this.selectedAccountIds.filter(item => item !== accountId);
      } else {
        newSelected = this.multiple ? [...this.selectedAccountIds, accountId] : [accountId];
      }

      // if selection is empty, and empty is not allowed, set selection to default again
      if (!this.allowEmpty && newSelected.length === 0) {
        this.handleResetClicked();
      } else {
        this.selectedAccountIds = newSelected;
        this.storeAndEmitSelection();
      }
    },
    /**
     * Reset the selection to the default value and store and emit the new selection
     * @returns {void}
     */
    handleResetClicked() {
      this.searchInput = null;
      const selected = this.sanitizeSelectedIds(this.default);
      if (!isEqual(selected, this.selectedAccountIds)) {
        this.selectedAccountIds = selected;
        this.storeAndEmitSelection();
      }
    },
    /**
     * Fetch accounts from the API and store them in the accounts array. Also initialize the selection.
     * @returns {void}
     */
    fetchAccounts() {
      this.isLoading = true;
      this.$store.dispatch('app/loadAccounts', {
        n1Accounts: this.n1Accounts
      }).then(accounts => {
        this.isLoading = false;
        this.accounts = accounts;
        this.$emit('accounts-loaded');
        this.initializeSelection();
      }).catch(error => {
        this.$snack(this.$i18n_t('Common.errorLoadingAccounts'), 'error');
        this.$emit('accounts-error');
        console.error(error);
      });
    }
  }
};